import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

interface FormData {
    name: string;
    company: string;
    role: string;
    email: string;
    industry: string;
    challenge: string;
}

const ContactForm: React.FC = () => {
    const [form, setForm] = useState<FormData>({
        name: '', company: '', role: '', email: '', industry: '', challenge: '',
    });
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const { language, t } = useLanguage();

    const industries = [
        t('form.indRetail'), t('form.indFinancial'), t('form.indLogistics'),
        t('form.indManufacturing'), t('form.indHealthcare'), t('form.indB2B'),
        t('form.indStartups'), t('form.indOther'),
    ];

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMsg('');
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
            await axios.post(`${baseUrl}/api/leads`, {
                name: form.name,
                email: form.email,
                company: form.company,
                role: form.role,
                industry: form.industry,
                message: form.challenge,
                source: 'contact-form',
                language: language === 'PT' ? 'PT' : language === 'ES' ? 'ES' : 'EN',
            });
            setStatus('success');
        } catch (err: any) {
            setStatus('error');
            const apiError = err?.response?.data?.error;
            const fieldErrors = apiError?.fields
                ? Object.values(apiError.fields as Record<string, string>).join('. ')
                : null;
            setErrorMsg(
                fieldErrors
                ?? apiError?.message
                ?? err?.response?.data?.message
                ?? 'Something went wrong. Please try again or email us directly.'
            );
        }
    };

    if (status === 'success') {
        return (
            <section className="ct-form-section" id="contact-form">
                <div className="container">
                    <div className="ct-success">
                        <div className="ct-success-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <div className="ct-success-label">{t('form.successLabel')}</div>
                        <h2 className="ct-success-title">{t('form.successTitle')}</h2>
                        <p className="ct-success-body">
                            {t('form.successBody')}
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="ct-form-section" id="contact-form">
            <div className="container">
                <form id="discoveryForm" onSubmit={handleSubmit}>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="name">{t('form.name')}</label>
                            <input type="text" id="name" name="name" placeholder={t('form.namePlaceholder')}
                                value={form.name} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="company">{t('form.company')}</label>
                            <input type="text" id="company" name="company" placeholder={t('form.companyPlaceholder')}
                                value={form.company} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="role">{t('form.role')}</label>
                            <input type="text" id="role" name="role" placeholder={t('form.rolePlaceholder')}
                                value={form.role} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">{t('form.email')}</label>
                            <input type="email" id="email" name="email" placeholder={t('form.emailPlaceholder')}
                                value={form.email} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="industry">{t('form.industry')}</label>
                        <select id="industry" name="industry" value={form.industry} onChange={handleChange}>
                            <option value="" disabled>{t('form.selectIndustry')}</option>
                            {industries.map(ind => (
                                <option key={ind}>{ind}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="challenge">{t('form.challenge')}</label>
                        <textarea id="challenge" name="challenge"
                            placeholder={t('form.challengePlaceholder')}
                            value={form.challenge} onChange={handleChange}
                        />
                    </div>

                    {status === 'error' && (
                        <p style={{ color: '#ef4444', fontSize: 14, marginBottom: 16 }}>{errorMsg}</p>
                    )}

                    <button type="submit" className="btn-primary form-submit" disabled={status === 'loading'}>
                        {status === 'loading' ? t('form.submitting') : t('form.submit')}
                        {status !== 'loading' && (
                            <svg viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                </form>

                <p className="form-email">
                    {t('form.directEmail')}{' '}
                    <a href="mailto:hello@perceptya.ai">hello@perceptya.ai</a>
                </p>
            </div>
        </section>
    );
};

export default ContactForm;
